import '../../i18n/autoTranslate';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { TrendingUp, Trophy, Flame, Code } from 'lucide-react';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import { API_BASE_URL, getAccessToken } from '../../../config/api';

const ProgressVisualization = () => {
  const navigate = useNavigate();

  // FIX: hoveredBar/hoveredPie are now strictly numeric indices or null.
  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredPie, setHoveredPie] = useState(null);
  const [loading, setLoading] = useState(true);

  const [weeklyData, setWeeklyData] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState(0);

  const [achievements, setAchievements] = useState([
    {
      title: 'Interview Ace',
      description: 'Scored 90+ in 5 interviews',
      progress: 0,
      icon: 'Trophy',
      color: 'accent',
      spotlight: 'rgba(139, 92, 246, 0.15)',
    },
    {
      title: 'Consistency King',
      description: '30-day practice streak',
      progress: 0,
      icon: 'Flame',
      color: 'primary',
      spotlight: 'rgba(59, 130, 246, 0.15)',
    },
    {
      title: 'Technical Master',
      description: 'Perfect technical scores',
      progress: 0,
      icon: 'Code',
      color: 'success',
      spotlight: 'rgba(16, 185, 129, 0.15)',
    },
  ]);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();

      const analyticsResponse = await fetch(`${API_BASE_URL}/api/interview/analytics/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        // FIX: defensive Array checks — backend can return null/object on error.
        setWeeklyData(Array.isArray(analyticsData?.weekly_data) ? analyticsData.weekly_data : []);
        setSkillsData(Array.isArray(analyticsData?.skills_data) ? analyticsData.skills_data : []);
        setWeeklyTrend(Number(analyticsData?.weekly_trend) || 0);
      }

      const achievementResponse = await fetch(`${API_BASE_URL}/api/interview/analytics/achievement-stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (achievementResponse.ok) {
        const achievementStats = await achievementResponse.json();
        setAchievements((prev) =>
          prev.map((achievement) => {
            if (achievement.title === 'Interview Ace') {
              return { ...achievement, progress: Number(achievementStats?.interview_ace) || 0 };
            }
            if (achievement.title === 'Consistency King') {
              return { ...achievement, progress: Number(achievementStats?.consistency_king) || 0 };
            }
            if (achievement.title === 'Technical Master') {
              return { ...achievement, progress: Number(achievementStats?.technical_master) || 0 };
            }
            return achievement;
          })
        );
      }
    } catch (err) {
      // Keep UI stable; logging only.
      // eslint-disable-next-line no-console
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'accent':
        return 'text-purple-400 bg-purple-400/10 border-purple-400/20';
      case 'primary':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
      case 'success':
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium">{label}</p>
          <p className="text-purple-400">Score: {payload[0].value}%</p>
          <p className="text-blue-400">Sessions: {payload[0].payload.sessions ?? 0}</p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium">{payload[0].name}</p>
          <p className="text-purple-400">Score: {payload[0].value}%</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-4 mt-4">
      {payload?.map((entry, index) => (
        <div key={`legend-${index}`} className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-sm text-gray-300">{entry.value}</span>
        </div>
      ))}
    </div>
  );

  const getIconComponent = (iconName) => {
    const icons = { Trophy, Flame, Code };
    const IconComponent = icons[iconName] || Trophy;
    return <IconComponent size={18} />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 h-96 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // FIX: guard `.some` against non-array shape.
  const hasWeeklyData =
    Array.isArray(weeklyData) && weeklyData.some((d) => Number(d?.sessions) > 0);
  const hasSkillsData = Array.isArray(skillsData) && skillsData.length > 0;

  return (
    <div className="space-y-6">
      {/* Weekly Performance Chart */}
      <SpotlightCard
        className="custom-spotlight-card rounded-2xl relative overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
        spotlightColor="rgba(99, 102, 241, 0.15)"
      >
        <div className="relative z-10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Weekly Performance</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <TrendingUp size={16} className={weeklyTrend >= 0 ? 'text-green-400' : 'text-red-400'} />
              <span>
                {weeklyTrend >= 0 ? '+' : ''}
                {weeklyTrend}% this week
              </span>
            </div>
          </div>

          <div className="w-full h-[300px]" aria-label="Weekly Performance Bar Chart">
            {hasWeeklyData ? (
              <ResponsiveContainer width="100%" height="100%">
                {/*
                  FIX: defs must be declared BEFORE the <Bar> that references the gradient,
                  otherwise Recharts can render the bars with no fill on first paint.
                  FIX: the chart-level onMouseMove was conflicting with onMouseOver/onMouseOut
                  on the Bar (mixing booleans and indices). Using only chart-level handlers now.
                */}
                <BarChart
                  data={weeklyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  onMouseMove={(state) => {
                    if (state && typeof state.activeTooltipIndex === 'number') {
                      setHoveredBar(state.activeTooltipIndex);
                    }
                  }}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#6366F1" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94A3B8', fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94A3B8', fontSize: 12 }}
                  />
                  <Tooltip
                    content={<CustomBarTooltip />}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                    {weeklyData.map((_, index) => (
                      <Cell
                        key={`bar-cell-${index}`}
                        fill="url(#barGradient)"
                        opacity={hoveredBar === null || hoveredBar === index ? 1 : 0.55}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 gap-3">
                <div className="h-12 w-12 rounded-full border border-white/10 bg-white/5 grid place-items-center">
                  <TrendingUp size={20} className="text-purple-300" />
                </div>
                <div className="text-sm">Complete interviews to see your weekly performance</div>
                <button
                  type="button"
                  onClick={() => navigate('/interview-setup-configuration')}
                  className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white transition"
                >
                  Start an Interview →
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/progress-analytics-insights')}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10 hover:text-white transition"
            >
              View Full Analytics →
            </button>
          </div>
        </div>
      </SpotlightCard>

      {/* Skills Breakdown */}
      <SpotlightCard
        className="custom-spotlight-card rounded-2xl relative overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
        spotlightColor="rgba(99, 102, 241, 0.15)"
      >
        <div className="relative z-10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Skills Breakdown</h3>

          {hasSkillsData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              {/*
                FIX: ResponsiveContainer needs an explicitly-sized parent.
                Was `min-h-[240px]` which lets the parent collapse to 0 in some
                grid contexts → pie chart wasn't rendering. Use a fixed height.
              */}
              <div className="w-full h-[260px]" aria-label="Skills Distribution Pie Chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={skillsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      onMouseEnter={(_, index) => setHoveredPie(index)}
                      onMouseLeave={() => setHoveredPie(null)}
                      isAnimationActive={false}
                    >
                      {skillsData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          stroke="rgba(30,30,46,0.8)"
                          strokeWidth={hoveredPie === index ? 3 : 1}
                          opacity={hoveredPie !== null && hoveredPie !== index ? 0.7 : 1}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend content={renderCustomLegend} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-3">
                {skillsData.map((skill, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: skill.color }}
                        />
                        <span className="text-sm font-medium text-white">{skill.name}</span>
                      </div>
                      <span className="text-sm text-gray-400">{skill.value}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-500 ease-smooth"
                        style={{
                          width: `${skill.value}%`,
                          backgroundColor: skill.color,
                          opacity: hoveredPie !== null && hoveredPie !== index ? 0.7 : 1,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm text-center px-6">
              Complete interviews to see your skills breakdown
            </div>
          )}
        </div>
      </SpotlightCard>

      {/* Achievement Progress */}
      <SpotlightCard
        className="custom-spotlight-card rounded-2xl relative overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
        spotlightColor="rgba(99, 102, 241, 0.15)"
      >
        <div className="relative z-10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Achievements</h3>
            <span className="text-xs text-gray-400">Progress (0–100)</span>
          </div>

          <div className="space-y-4">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-start space-x-3">
                    <div
                      className={`flex items-center justify-center w-16 h-16 rounded-xl border ${getColorClass(
                        achievement.color
                      )}`}
                    >
                      {getIconComponent(achievement.icon)}
                    </div>

                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-white">{achievement.title}</h4>
                        <span className="text-xs text-gray-400">
                          {Math.max(0, Math.min(100, Number(achievement.progress) || 0))}/100
                        </span>
                      </div>
                      <p className="text-xs text-gray-300">{achievement.description}</p>

                      <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ease-smooth ${
                            achievement.color === 'accent'
                              ? 'bg-purple-500'
                              : achievement.color === 'primary'
                                ? 'bg-blue-500'
                                : achievement.color === 'success'
                                  ? 'bg-green-500'
                                  : 'bg-gray-400'
                          }`}
                          style={{
                            width: `${Math.max(0, Math.min(100, Number(achievement.progress) || 0))}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SpotlightCard>
    </div>
  );
};

export default ProgressVisualization;
